import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import "./WarehouseAssortmentList.css";

const WarehouseAssortmentListComponent = (props) => {
  const navigate = useNavigate();
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [selectedRuleFailures, setSelectedRuleFailures] = useState([]);
  const [selectedWarehouseName, setSelectedWarehouseName] = useState("");

  const {
    assortmentWarehouseMetaDataList,
    handleWarehouseClick,
    loading,
    error,
    searchTerm,
    onSearchChange,
    onClearSearch,
    hasMore,
    onLoadMore
  } = props;

  // Date formatter - exactly like Page 1
  const formatDate = (dateString) => {
    if (!dateString || dateString === "null") return "Not set";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";
      
      const day = date.getDate();
      const suffix = getNumberSuffix(day);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}${suffix} ${month} ${year}`;
    } catch (error) {
      return "Invalid date";
    }
  };

  const getNumberSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  // Get failure info based on type - Page 1 style
  const getFailureInfo = (failure) => {
    const type = failure.failureType;
    const items = failure.failedItems || {};

    if (type?.includes('not_present')) {
      const entity = type.split('_')[0];
      return {
        message: `${entity.charAt(0).toUpperCase() + entity.slice(1)} not present in assortment`,
        cls: 'missing'
      };
    }
    if (type?.includes('restricted')) {
      const entity = type.split('_')[0];
      return {
        message: `${entity.charAt(0).toUpperCase() + entity.slice(1)} restricted`,
        cls: 'restricted'
      };
    }
    if (type === 'offer_price_breached') {
      return {
        message: `Offer price breached - Max: ₹${items.maxOfferPrice}`,
        cls: 'breached'
      };
    }
    return { message: 'Rule failed', cls: '' };
  };

  // Format IDs for display
  const formatIds = (items) => {
    if (!items?.failRuleItemDetails?.length) {
      if (items?.maxOfferPrice) return `₹${items.maxOfferPrice}`;
      return '—';
    }
    const ids = items.failRuleItemDetails.map(i => i.id);
    if (ids.length === 1) return ids[0].toString();
    return `${ids.slice(0, 2).join(', ')}${ids.length > 2 ? ` +${ids.length - 2}` : ''}`;
  };

  // Format names for display
  const formatNames = (items) => {
    if (!items?.failRuleItemDetails?.length) return '—';
    const names = items.failRuleItemDetails.map(i => i.name);
    if (names.length === 1) return names[0];
    return `${names.slice(0, 2).join(', ')}${names.length > 2 ? ` +${names.length - 2}` : ''}`;
  };

  // Get image URL
  const getImageUrl = (path) => path ? `https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${path}` : null;

  // Rule status - exactly like Page 1
  const getRuleStatus = (item) => {
    const totalRules = item.total_rules || 0;
    const ruleFailures = item.rule_failure_details || [];
    const failedCount = ruleFailures.length;

    if (failedCount === 0 && totalRules > 0) {
      return {
        text: "All Pass",
        className: "all-pass",
        isClickable: false
      };
    }

    if (failedCount > 0) {
      return {
        text: `${failedCount.toString().padStart(2, '0')} Fail`,
        className: "some-fail",
        isClickable: true
      };
    }

    return {
      text: "No Rules",
      className: "no-rules",
      isClickable: false
    };
  };

  const handleCardHeaderClick = (e, reference_id) => {
    e.stopPropagation();
    handleWarehouseClick(reference_id);
  };

  const handleRuleClick = (e, item) => {
    e.stopPropagation();
    setSelectedRuleFailures(item.rule_failure_details || []);
    setSelectedWarehouseName(item.reference_name);
    setOpenRuleDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenRuleDialog(false);
    setSelectedRuleFailures([]);
    setSelectedWarehouseName("");
  };

  if (loading && assortmentWarehouseMetaDataList.length === 0) {
    return (
      <div className="warehouse-list-page">
        <Sidebar />
        <div className="warehouse-list-content">
          <div className="warehouse-loading-container">
            <CircularProgress size={60} thickness={4} />
            <p>Loading warehouses...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="warehouse-list-page">
        <Sidebar />
        <div className="warehouse-list-content">
          <div className="warehouse-error-container">
            <div className="warehouse-error-icon">!</div>
            <h3>Error loading data</h3>
            <p>{error}</p>
            <button 
              className="warehouse-retry-btn" 
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="warehouse-list-page">
      <Sidebar />
      <div className="warehouse-list-content">
        {/* Header - Exactly like Page 1 */}
        <div className="warehouse-list-header">
          <div className="warehouse-header-left">
            <button className="warehouse-back-btn" onClick={() => navigate(-1)}>
              ←
            </button>
            <div className="warehouse-header-title-section">
              <h1 className="warehouse-list-title">Warehouses</h1>
            </div>
          </div>
        </div>

        {/* Search - Exactly like Page 1 */}
        <div className="warehouse-list-search">
          <SearchIcon className="warehouse-list-search-icon" />
          <input
            type="text"
            placeholder="Search by Warehouse Name, Machine ID, Name"
            value={searchTerm}
            onChange={onSearchChange}
            className="warehouse-list-search-input"
          />
          {searchTerm && (
            <button 
              className="warehouse-clear-search-btn"
              onClick={onClearSearch}
            >
              ✕
            </button>
          )}
        </div>

        {/* Grid - Exactly like Page 1 */}
        <div className="warehouse-list-grid">
          {assortmentWarehouseMetaDataList && assortmentWarehouseMetaDataList.length > 0 ? (
            <>
              {assortmentWarehouseMetaDataList.map((item) => {
                const ruleStatus = getRuleStatus(item);
                const activeStartDate = formatDate(item.active_assortment_start_date);
                 const scheduledStartDate= formatDate(item.schedule_assortment_start_date)
                
                return (
                  <div
                    className="warehouse-list-card"
                    key={item.reference_id}
                  >
                    {/* Card Header - Exactly like Page 1 */}
                    <div 
                      className="warehouse-card-header clickable"
                      onClick={(e) => handleCardHeaderClick(e, item.reference_id)}
                    >
                      <div className="warehouse-card-header-content">
                        <div className="warehouse-name-with-icon">
                          <LocationOnIcon className="warehouse-location-icon" />
                          <h3 className="warehouse-name">{item.reference_name}</h3>
                        </div>
                        <ChevronRightIcon className="warehouse-card-arrow-icon" />
                      </div>
                    </div>
                    
                    {/* Status Container - Exactly like Page 1 */}
                    <div className="warehouse-status-container">
                      <div className="warehouse-status-item">
                        <div className="warehouse-status-label-section">
                          <span className="warehouse-status-bullet active"></span>
                          <span className="warehouse-status-label-active">Active</span>
                        </div>
                        <span className="warehouse-status-date-text">
                          Start Date: <strong>{activeStartDate}</strong>
                        </span>
                      </div>
                      
                      <div className="warehouse-status-item">
                        <div className="warehouse-status-label-section">
                          <span className="warehouse-status-bullet scheduled"></span>
                          <span className="warehouse-status-label-scheduled">Scheduled</span>
                        </div>
                        
                       <span className="warehouse-status-date-text">
                          Start Date: <strong>{scheduledStartDate}</strong>
                        </span>
                      </div>
                    </div>

                 
<div className="warehouse-rules-section">
  <span className="warehouse-rules-label">Applied Rules:</span>
  <span className="warehouse-rules-number">
    {(item.total_rules || 0).toString().padStart(2, '0')}
  </span>
  <span className="warehouse-rules-separator">–</span>
  <div 
    className={`warehouse-rules-status-badge ${ruleStatus.className}`}
    onClick={(e) => ruleStatus.isClickable ? handleRuleClick(e, item) : null}
    style={ruleStatus.isClickable ? { cursor: 'pointer' } : {}}
  >
    {ruleStatus.text}
  </div>
</div>
                  </div>
                );
              })}

              {/* Load More Button */}
              {hasMore && (
                <div className="warehouse-load-more-container">
                  <button 
                    className="warehouse-load-more-btn"
                    onClick={onLoadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} thickness={4} />
                        <span>Loading...</span>
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            // No Results - Exactly like Page 1
            <div className="warehouse-no-results">
              <div className="warehouse-no-results-icon">📦</div>
              <h3>No warehouses found</h3>
              <p>{searchTerm ? `No results for "${searchTerm}"` : "No warehouses available"}</p>
              {searchTerm && (
                <button 
                  className="warehouse-clear-search-btn-large"
                  onClick={onClearSearch}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>

        {/* Rule Failure Dialog - Exactly like Page 1 */}
        <div className={`warehouse-rule-dialog-overlay ${openRuleDialog ? 'active' : ''}`} onClick={handleCloseDialog}>
          <div className="warehouse-rule-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="warehouse-rule-dialog-header">
              <div className="warehouse-rule-dialog-title">
                <div className="warehouse-rule-dialog-icon-wrapper">
                  <LocationOnIcon />
                </div>
                <div>
                  <h2>{selectedWarehouseName}</h2>
                  <p>
                    <span className="warehouse-failure-count">{selectedRuleFailures.length}</span>
                    Rule{selectedRuleFailures.length !== 1 ? 's' : ''} Failed
                  </p>
                </div>
              </div>
              <button className="warehouse-rule-dialog-close" onClick={handleCloseDialog}>
                <CloseIcon />
              </button>
            </div>

            <div className="warehouse-rule-dialog-content">
              {/* Timestamp - Exactly like Page 1 */}
              {/* <div className="warehouse-rule-timestamp">
                <span>Last updated at {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}, Today</span>
              </div> */}

              {/* Table - Exactly like Page 1 (No expand/collapse) */}
              <div className="warehouse-rule-table-container">
                <table className="warehouse-rule-table">
                  <thead>
                    <tr>
                      <th>Rule ID</th>
                      <th>Type</th>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Status Message</th>
                      <th>Product Image</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRuleFailures.length > 0 ? (
                      selectedRuleFailures.map((failure, index) => {
                        const info = getFailureInfo(failure);
                        const items = failure.failedItems || {};
                        const firstItem = items.failRuleItemDetails?.[0] || {};
                        
                        return (
                          <tr key={index}>
                            <td>
                              <span className="warehouse-rule-id">{failure.ruleId || '—'}</span>
                            </td>
                            <td>
                              <span className={`warehouse-rule-type ${failure.failureType}`}>
                                {failure.ruleType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            </td>
                            <td>
                              <span className="warehouse-rule-ids">
                                {formatIds(items)}
                              </span>
                            </td>
                            <td>
                              <span className="warehouse-rule-names">
                                {formatNames(items)}
                              </span>
                            </td>
                            <td>
                              <div className="warehouse-rule-message">
                                {/* <span className={`warehouse-status-indicator ${failure.failureType}`}></span> */}
                                <span className="warehouse-message-text">{info.message}</span>
                              </div>
                            </td>
                            <td>
                              {firstItem?.image ? (
                                <img 
                                  src={getImageUrl(firstItem.image)}
                                  alt=""
                                  className="warehouse-product-thumbnail"
                                />
                              ) : (
                                <div style={{ 
                                  width: 40, 
                                  height: 40, 
                                  background: '#F3F4F6', 
                                  borderRadius: 8,
                                  margin: '0 auto'
                                }}></div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="warehouse-rule-empty">
                          <div className="warehouse-rule-empty-state">
                            <CheckCircleIcon className="warehouse-rule-empty-icon" />
                            <h4>No Rule Failures</h4>
                            <p>All rules have passed successfully</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="warehouse-rule-dialog-footer">
              <button className="warehouse-rule-save-btn" onClick={handleCloseDialog}>
               Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseAssortmentListComponent;